/**
 * The panel, and its nine states.
 *
 * It opens on search and not on a form (FR-27), and that is the decision the
 * whole surface rests on: part of what arrives as feedback is not feedback but
 * not knowing how, and an article answers that for nothing while a report
 * costs a triage slot, an answer and a disappointment. What the choice measures
 * as a side effect is the other half: a search that finds nothing is a missing
 * article, with the words, the application and the screen attached (FR-28), and
 * that list is the work queue for the help itself.
 *
 * Every state scrolls inside its own box. All eight were drawn 420 by 620 with
 * hidden overflow, so an article of two screens or a fourth message in a thread
 * simply disappeared. The panel is 620 tall on every screen and its contents
 * are not.
 *
 * There is no skeleton and no spinner of its own. A button that is sending says
 * so in its label, because a turning circle says nothing to a screen reader and
 * colour says nothing to somebody who cannot see it (WCAG 1.4.1); a list that
 * is refreshing keeps the view it had and carries aria-busy. platform-ui has
 * no list loader, and a component goes into the shared package at its second
 * consumer, never at its first.
 */
import { Button, Checkbox, Radio, RadioGroup, TextArea, TextField } from "@depicomnlbv/platform-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import { Thread } from "./Thread";
import { queryKeys } from "./client";
import { copy } from "./copy";
import { useFeedback } from "./context";
import { captureScreenshot } from "./screenshot";
import type { SubmissionSummary } from "./types";
import {
  ContextCard,
  Icon,
  KindChip,
  StateChip,
  countedSentence,
  fill,
  searchedFor,
  sentInBy,
} from "./ui";

/** Where the reader is inside the panel. One per artboard, plus the list. */
type View =
  | { at: "help" }
  | { at: "article"; slug: string }
  | { at: "form"; intent: "report" | "howto" }
  | { at: "sent"; id: number }
  | { at: "asked" }
  | { at: "list" }
  | { at: "thread"; id: number };

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/** A search of one letter matches everything, so it is not a search yet. */
const ENOUGH_TO_SEARCH = 2;

function useDebounced(value: string, delay = 250): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

function onDay(at: string): string {
  return new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "long" });
}

export function Panel({
  onClose,
  openOn = "help",
}: {
  onClose: () => void;
  /**
   * Which of the two entries opened this panel, and therefore where it starts.
   *
   * `help` is the launcher, and the reason it opens on search rather than on a
   * form is on the canvas: part of what arrives as feedback is somebody not
   * knowing how, and an article answers that for nothing while a report costs
   * a triage slot, an answer and a disappointment.
   *
   * `report` is the button on a list view, which the copy lexicon fixes as
   * "the button that starts a submission from a list view". The same lexicon
   * says why it is not called Report it instead: a list carries no offer to
   * decline, because no help was offered on that path. So it starts on the
   * form. Two entries, two starting points, one panel.
   *
   * Deliberately not the `View` union itself. That type is this file's own
   * business and exposing it would let a caller open the panel on a thread it
   * has no id for.
   */
  openOn?: "help" | "report";
}) {
  const { client, currentScreen, build, rasterise } = useFeedback();
  const queryClient = useQueryClient();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<View>(
    openOn === "report" ? { at: "form", intent: "report" } : { at: "help" },
  );

  /**
   * Backing out of the form, which lands somewhere else per entry.
   *
   * From the launcher the reader arrived through the help and declined it, so
   * cancelling means the help he came from. From a list view nothing is behind
   * the form, so cancelling means closing the panel; sending him to a help he
   * never asked for would be a screen appearing out of a cancel. For the same
   * reason the "Back to help" link above is not drawn on that entry: it would
   * name a screen the reader was never on.
   */
  const leaveTheForm =
    openOn === "report" ? onClose : () => setView({ at: "help" });
  const [terms, setTerms] = useState("");
  const [typeCode, setTypeCode] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [withScreenshot, setWithScreenshot] = useState(true);
  const [sendFailed, setSendFailed] = useState(false);

  const settledTerms = useDebounced(terms);
  const searching = settledTerms.trim().length >= ENOUGH_TO_SEARCH;

  const me = useQuery({ queryKey: queryKeys.me, queryFn: () => client.me() });
  const types = useQuery({ queryKey: queryKeys.types, queryFn: () => client.types() });
  const articles = useQuery({ queryKey: queryKeys.articles, queryFn: () => client.articles() });
  const results = useQuery({
    queryKey: queryKeys.help(settledTerms, currentScreen),
    queryFn: () => client.searchHelp(settledTerms, currentScreen),
    enabled: searching,
  });
  const mine = useQuery({
    queryKey: queryKeys.submissions("mine"),
    queryFn: () => client.submissions("mine"),
    enabled: view.at === "list",
  });
  const article = useQuery({
    queryKey: queryKeys.article(view.at === "article" ? view.slug : ""),
    queryFn: () => client.article(view.at === "article" ? view.slug : ""),
    enabled: view.at === "article",
  });
  const openId = view.at === "thread" ? view.id : view.at === "sent" ? view.id : 0;
  const thread = useQuery({
    queryKey: queryKeys.submission(openId),
    queryFn: () => client.submission(openId),
    enabled: openId !== 0,
  });

  const chosenType = typeCode ?? types.data?.[0]?.code ?? null;

  /**
   * The picture of the page, after the words are safely written (FR-21).
   *
   * It runs after the submission, because the image belongs to a row that has
   * to exist first, and it never turns a sent report into a failed one: by the
   * time this runs the reader has been told his words arrived, and a red panel
   * because a raster did not fit would be a lie about what happened. So both
   * halves are caught and the reason goes to the console for whoever looks.
   *
   * The panel is left out of the picture. A screenshot of the report form on
   * top of the screen it is about is a picture of the wrong thing.
   *
   * `captureScreenshot` answers null when even the smallest scale is over the
   * cap, and the report stands without one: a report without a picture is a
   * report.
   */
  async function sendTheScreenshot(submissionId: number): Promise<void> {
    if (!withScreenshot) return;
    try {
      const image = await captureScreenshot({
        rasterise,
        page: document.body,
        hide: panelRef.current ? [panelRef.current] : [],
      });
      if (image) await client.screenshot(submissionId, image);
    } catch (failure) {
      // eslint-disable-next-line no-console
      console.warn("feedback: the screenshot did not go with the report", failure);
    }
  }

  const send = useMutation({
    mutationFn: async () => {
      // No `withScreenshot` here. The checkbox decides whether this panel takes
      // a picture at all, and the picture arrives on its own route afterwards.
      // Sending the flag with the submission looked right and did nothing:
      // SubmissionCreate sets extra="ignore", so it crossed the wire and was
      // dropped in silence. Found by check_request_shapes_match.py.
      const submission = await client.submit({
        typeCode: chosenType ?? "",
        body: body.trim(),
        screen: currentScreen,
        build,
      });
      return submission;
    },
    onSuccess: (submission: SubmissionSummary) => {
      setSendFailed(false);
      setBody("");
      void queryClient.invalidateQueries({ queryKey: ["feedback", "submissions"] });
      setView({ at: "sent", id: submission.id });
      // After the confirmation and not before it. The words are written and the
      // reader has been told so; making him watch a rasteriser finish before he
      // sees that would trade the thing he cares about for the thing he does
      // not. It settles nothing on screen either way (FR-21).
      void sendTheScreenshot(submission.id);
    },
    // The reader's text is not cleared and the form is not left. Somebody who
    // loses what he typed types it once more and says nothing the third time
    // (FR-1), and then the whole surface is decoration.
    onError: () => setSendFailed(true),
  });

  const ask = useMutation({
    mutationFn: () => client.askForHowTo(settledTerms, currentScreen),
    onSuccess: () => setView({ at: "asked" }),
  });

  const reply = useMutation({
    mutationFn: (message: string) => client.addMessage(openId, message),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.submission(openId) });
    },
  });

  /**
   * The keyboard half of FR-18. Escape closes, and Tab stays inside: a reader
   * whose focus wanders behind a panel he cannot see the focus ring in has no
   * way back to it.
   *
   * aria-modal is deliberately not set. The drawing has no scrim, so the rest
   * of the page really is still there and still clickable, and telling assistive
   * technology otherwise would be a claim the surface does not keep.
   */
  useEffect(() => {
    const node = panelRef.current;
    if (!node) return;
    const first = node.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, []);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const node = panelRef.current;
    if (!node) return;
    const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (element) => element.offsetParent !== null || element === document.activeElement,
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function startReporting(intent: "report" | "howto") {
    setSendFailed(false);
    setView({ at: "form", intent });
  }

  return (
    <div
      className="pf-panel"
      role="dialog"
      aria-labelledby={titleId}
      ref={panelRef}
      onKeyDown={onKeyDown}
    >
      <div className="pf-panel__head">
        <div>
          <p className="pf-panel__title" id={titleId}>
            {copy.panel.title}
          </p>
          <p className="pf-panel__subtitle">{copy.panel.subtitle}</p>
        </div>
        <Button variant="subtle" aria-label={copy.panel.close} onPress={onClose}>
          <Icon name="close" />
        </Button>
      </div>

      {view.at === "help" ? (
        <>
          <div className="pf-panel__body" aria-busy={results.isFetching}>
            {/* The canvas drew "What are you trying to do?" inside the field
                until 9 September. TextField has no placeholder prop, and that
                is deliberate rather than missing: a placeholder disappears as
                somebody types and is read as a value that is already there.
                The words are the description slot instead, under the field,
                and the visible label is what FR-31 asks for. */}
            <TextField
              type="search"
              label={copy.search.label}
              description={copy.search.description}
              value={terms}
              onChange={setTerms}
            />

            {!searching ? (
              <div className="pf-scroller">
                <p className="pf-section-label">{copy.start.helpHeading}</p>
                <div className="pf-stack">
                  {(articles.data ?? []).map((item) => (
                    <button
                      type="button"
                      className="pf-card"
                      key={item.slug}
                      onClick={() => setView({ at: "article", slug: item.slug })}
                    >
                      <span className="pf-card__title">{item.title}</span>
                      <span className="pf-card__summary">{item.summary}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (results.data?.articles.length ?? 0) > 0 ? (
              <div className="pf-scroller">
                <p className="pf-section-label">{copy.results.heading}</p>
                <div className="pf-stack">
                  {(results.data?.articles ?? []).map((item) => (
                    <button
                      type="button"
                      className="pf-card"
                      key={item.slug}
                      onClick={() => setView({ at: "article", slug: item.slug })}
                    >
                      <span className="pf-card__title">{item.title}</span>
                      <span className="pf-card__summary">{item.summary}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : results.data ? (
              <>
                <div className="pf-blank">
                  <Icon name="search" />
                  <p className="pf-blank__title">{copy.noResult.title}</p>
                  {/* The sentence tells the reader his search is on the list,
                      so it is drawn when the server says a row was written and
                      not before. It rendered unconditionally until 9 September
                      2026 while `missRecorded` sat on the type and was read by
                      nothing, so the screen was hoping in exactly the words
                      three comments said it did not have to. A write that was
                      refused now leaves the title and the way out standing and
                      drops the claim. */}
                  {results.data.missRecorded ? (
                    <p className="pf-blank__body">{copy.noResult.body}</p>
                  ) : null}
                </div>
                {/* FR-28, FR-30. The count is the people who searched these
                    words and found nothing, and the write puts this reader
                    among them, so `missRecorded` is what says he is inside the
                    number. It read "{count} people searched for this too."
                    until 9 September 2026, which claimed one person too many
                    at every value and was false at one, where the reader is
                    the only searcher. The guard stays on the count and not on
                    the reader: zero is only reachable when the write was
                    refused, and then there is nothing measured to report. */}
                {results.data.alsoSearched > 0 ? (
                  <p className="pf-note">
                    {searchedFor(
                      {
                        total: copy.noResult.peopleSearched,
                        singular: copy.noResult.peopleSearchedSingular,
                        nobodyElse: copy.noResult.nobodyElseSearched,
                        includingYou: copy.list.includingYou,
                      },
                      results.data.alsoSearched,
                      results.data.missRecorded,
                    )}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>

          <div className="pf-panel__foot">
            {!searching ? (
              <button type="button" className="pf-footlink" onClick={() => setView({ at: "list" })}>
                {copy.thread.back}
              </button>
            ) : (
              <span className="pf-foot-note">
                {(results.data?.articles.length ?? 0) > 0
                  ? copy.results.noneOfThese
                  : copy.noResult.askForIt}
              </span>
            )}
            {searching && results.data && results.data.articles.length === 0 ? (
              <Button
                variant="primary"
                isPending={ask.isPending}
                pendingLabel={copy.form.sending}
                onPress={() => ask.mutate()}
              >
                {copy.action.askForAHowTo}
              </Button>
            ) : (
              <Button variant="primary" onPress={() => startReporting("report")}>
                {copy.action.reportInstead}
              </Button>
            )}
          </div>
        </>
      ) : null}

      {view.at === "asked" ? (
        <>
          <div className="pf-panel__body">
            <div className="pf-banner pf-banner--good">
              <Icon name="tick" />
              <div>
                <p className="pf-banner__title">{copy.sent.title}</p>
                <p className="pf-banner__body">{copy.noResult.body}</p>
              </div>
            </div>
          </div>
          <div className="pf-panel__foot pf-panel__foot--end">
            <Button variant="secondary" onPress={() => setView({ at: "help" })}>
              {copy.article.back}
            </Button>
          </div>
        </>
      ) : null}

      {view.at === "article" ? (
        <>
          <div className="pf-panel__body">
            <button type="button" className="pf-back" onClick={() => setView({ at: "help" })}>
              <Icon name="back" /> {copy.article.back}
            </button>
            <div>
              <h2 className="pf-article__title">{article.data?.title ?? ""}</h2>
              {article.data ? (
                <p className="pf-article__meta">
                  {fill(copy.article.meta, {
                    owner: article.data.owner,
                    date: onDay(article.data.lastReviewedAt),
                  })}
                </p>
              ) : null}
            </div>
            <p className="pf-article__body">{article.data?.body ?? ""}</p>
          </div>
          <div className="pf-panel__foot">
            <span className="pf-foot-note">{copy.article.stillStuck}</span>
            <Button variant="secondary" onPress={() => startReporting("report")}>
              {copy.action.reportInstead}
            </Button>
          </div>
        </>
      ) : null}

      {view.at === "form" ? (
        <>
          <div className="pf-panel__body">
            {openOn === "report" ? null : (
              <button type="button" className="pf-back" onClick={() => setView({ at: "help" })}>
                <Icon name="back" /> {copy.article.back}
              </button>
            )}

            {sendFailed ? (
              <div className="pf-banner pf-banner--bad" role="alert">
                <Icon name="warn" />
                <div>
                  <p className="pf-banner__title">{copy.error.title}</p>
                  <p className="pf-banner__body">{copy.error.body}</p>
                </div>
              </div>
            ) : null}

            <RadioGroup
              className="pf-kinds"
              label={copy.form.kindLabel}
              orientation="horizontal"
              value={chosenType ?? ""}
              onChange={setTypeCode}
            >
              {(types.data ?? []).map((type) => (
                <Radio value={type.code} key={type.code}>
                  {type.label}
                  {/* The sublabel stays. Spec section 13 point 6: it carries the
                      distinction, and it carries it exactly where the reporter
                      is already inside the software. */}
                  <span className="pf-card__summary">{type.sublabel}</span>
                </Radio>
              ))}
            </RadioGroup>

            <TextArea
              label={copy.form.bodyLabel}
              value={body}
              onChange={setBody}
              rows={4}
            />

            <ContextCard
              heading={copy.context.heading}
              rows={[
                { key: copy.context.you, value: me.data?.name ?? "" },
                { key: copy.context.application, value: me.data?.application.name ?? "" },
                { key: copy.context.screen, value: currentScreen, code: true },
                { key: copy.context.version, value: build, code: true },
              ]}
            />

            <Checkbox isSelected={withScreenshot} onChange={setWithScreenshot}>
              {copy.form.screenshot}
            </Checkbox>
          </div>

          <div className="pf-panel__foot">
            <div className="pf-actions">
              <Button
                variant="primary"
                isPending={send.isPending}
                pendingLabel={copy.form.sending}
                isDisabled={body.trim().length === 0 || !chosenType}
                onPress={() => send.mutate()}
              >
                {sendFailed ? copy.error.tryAgain : copy.form.send}
              </Button>
              <Button variant="secondary" onPress={leaveTheForm}>
                {copy.form.cancel}
              </Button>
            </div>
            <span className="pf-foot-note">{copy.form.promise}</span>
          </div>
        </>
      ) : null}

      {view.at === "sent" && thread.data ? (
        <Thread
          messages={thread.data.messages}
          isSending={reply.isPending}
          onSend={(message) => reply.mutate(message)}
          above={
            <>
              <button type="button" className="pf-back" onClick={() => setView({ at: "list" })}>
                <Icon name="back" /> {copy.thread.back}
              </button>
              <div className="pf-banner pf-banner--good">
                <Icon name="tick" />
                <div>
                  <p className="pf-banner__title">{copy.sent.title}</p>
                  <p className="pf-banner__body">{copy.sent.body}</p>
                </div>
              </div>
            </>
          }
          below={
            <div className="pf-outcome">
              <ContextCard
                heading={copy.context.heading}
                rows={[
                  { key: copy.context.you, value: me.data?.name ?? "" },
                  { key: copy.context.application, value: me.data?.application.name ?? "" },
                  { key: copy.context.screen, value: thread.data.screen ?? "", code: true },
                  { key: copy.context.version, value: thread.data.build ?? "", code: true },
                ]}
              />
            </div>
          }
        />
      ) : null}

      {view.at === "thread" && thread.data ? (
        <Thread
          messages={thread.data.messages}
          isSending={reply.isPending}
          onSend={(message) => reply.mutate(message)}
          above={
            <>
              <button type="button" className="pf-back" onClick={() => setView({ at: "list" })}>
                <Icon name="back" /> {copy.thread.back}
              </button>
              <div className="pf-item-title">
                <h2 className="pf-item-title__text">{thread.data.title}</h2>
                {thread.data.state ? <StateChip chip={thread.data.state} /> : null}
              </div>
            </>
          }
          below={
            <div className="pf-outcome">
              <div className="pf-outcome__row">
                <span className="pf-outcome__key">{copy.thread.whereItWent}</span>
                <span className="pf-outcome__value pf-outcome__value--link">
                  {thread.data.destinationName ?? ""}
                </span>
              </div>
              <div className="pf-outcome__row">
                <span className="pf-outcome__key">{copy.thread.liveWhen}</span>
                <span className="pf-outcome__value">
                  {thread.data.liveSince
                    ? fill(copy.everything.liveSince, { date: onDay(thread.data.liveSince) })
                    : copy.thread.notLiveYet}
                </span>
              </div>
            </div>
          }
        />
      ) : null}

      {view.at === "list" ? (
        <>
          <div className="pf-panel__body" aria-busy={mine.isFetching}>
            <div className="pf-tabs">
              <Button variant="secondary" aria-pressed={false} onPress={() => setView({ at: "help" })}>
                {copy.list.tabEverything}
              </Button>
              <Button variant="primary" aria-pressed>
                {copy.list.tabFromYou}
              </Button>
            </div>

            {(mine.data?.length ?? 0) === 0 ? (
              <div className="pf-blank">
                <Icon name="bubble" />
                <p className="pf-blank__title">{copy.empty.title}</p>
                <p className="pf-blank__body">{copy.empty.body}</p>
              </div>
            ) : (
              <div className="pf-stack">
                {(mine.data ?? []).map((item: SubmissionSummary) => (
                  <button
                    type="button"
                    className="pf-card"
                    key={item.id}
                    onClick={() => setView({ at: "thread", id: item.id })}
                  >
                    <span className="pf-card__chips">
                      <KindChip chip={item.kind} />
                      {item.state ? <StateChip chip={item.state} /> : null}
                    </span>
                    <span className="pf-card__title">{item.title}</span>
                    <span className="pf-card__meta">
                      {onDay(item.submittedAt)}
                      {" · "}
                      {sentInBy(
                        {
                          total: copy.list.sentInBy,
                          singular: copy.list.sentInBySingular,
                          you: copy.list.sentInByYou,
                          includingYou: copy.list.includingYou,
                        },
                        item.sentInBy,
                        item.includesYou,
                      )}
                    </span>
                    {item.unreadAnswers > 0 ? (
                      <span className="pf-card__answers">
                        <Icon name="bubble" />
                        {countedSentence(
                          {
                            total: copy.list.answersToReadPlural,
                            singular: copy.list.answersToRead,
                          },
                          item.unreadAnswers,
                        )}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pf-panel__foot pf-panel__foot--end">
            <Button variant="primary" onPress={() => startReporting("report")}>
              {copy.action.reportSomething}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
